import React, { useEffect, useState } from 'react';
import { ArrowLeft, Check, AlertCircle } from 'lucide-react';
import { useQuote } from '../context/QuoteContext';
import ClarifyingQuestions from './ClarifyingQuestions';
import PriceRangeWidget from './PriceRangeWidget';
import { analyzeWorkRequest } from '../services/openai';
import { saveQuoteEstimate, saveClarifyingQuestions } from '../services/database';

interface PriceEstimateProps {
  onNext: () => void;
  onBack: () => void;
}

const PriceEstimate: React.FC<PriceEstimateProps> = ({ onNext, onBack }) => {
  const { state, dispatch } = useQuote();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showQuestions, setShowQuestions] = useState(false);
  const [priceRange, setPriceRange] = useState<{
    min: number;
    max: number;
    confidence: 'low' | 'medium' | 'high';
  } | null>(null);
  const [isRefining, setIsRefining] = useState(false);
  const [hasInitializedEstimate, setHasInitializedEstimate] = useState(false);
  
  useEffect(() => {
    const getPriceEstimate = async () => {
      if (!state.workDescription?.description || hasInitializedEstimate) {
        return;
      }
      setHasInitializedEstimate(true);
      
      setIsLoading(true);
      setError(null);
      
      try {
        const analysis = await analyzeWorkRequest(
          state.workDescription.description,
          state.workDescription.category
        );
        
        setPriceRange({
          min: analysis.priceRange.min,
          max: analysis.priceRange.max,
          confidence: analysis.confidence || 'low'
        });
        
        dispatch({ type: 'SET_PRICE_RANGE', payload: analysis.priceRange });
        
        if (analysis.clarifyingQuestions?.length) {
          dispatch({ 
            type: 'SET_CLARIFYING_QUESTIONS', 
            payload: analysis.clarifyingQuestions 
          });

          // Save the AI-generated questions
          if (state.workDescription.id) {
            await saveClarifyingQuestions(
              state.workDescription.id,
              analysis.clarifyingQuestions
            );
          }

          setShowQuestions(true);
        }

        // Save the estimate to the database
        if (state.workDescription.id) {
          await saveQuoteEstimate(
            state.workDescription.id,
            analysis.priceRange.min,
            analysis.priceRange.max,
            analysis.confidence || 'low'
          );
        }

      } catch (err) {
        console.error('Price estimate error:', err);
        setError(err instanceof Error ? err.message : 'Error generating price estimate. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };
    
    getPriceEstimate();
  }, [state.workDescription?.description, state.workDescription?.category, dispatch, hasInitializedEstimate]);

  const handleQuestionsComplete = () => {
    setShowQuestions(false);
  };

  const handlePriceUpdate = async (newPriceRange: typeof priceRange) => {
    setIsRefining(true);
    setTimeout(async () => {
      setPriceRange(newPriceRange);
      
      // Save the refined estimate and updated answers
      if (state.workDescription?.id && newPriceRange) {
        await saveQuoteEstimate(
          state.workDescription.id,
          newPriceRange.min,
          newPriceRange.max,
          newPriceRange.confidence
        );

        // Save the user's answers to the clarifying questions
        await saveClarifyingQuestions(
          state.workDescription.id,
          state.clarifyingQuestions
        );
      }
      
      setIsRefining(false);
    }, 500);
  };
  
  if (showQuestions) {
    return (
      <div className="space-y-6">
        {priceRange && (
          <PriceRangeWidget
            minPrice={priceRange.min}
            maxPrice={priceRange.max}
            confidence={priceRange.confidence}
            isRefining={isRefining}
          />
        )}
        <ClarifyingQuestions 
          onComplete={handleQuestionsComplete}
          onPriceUpdate={handlePriceUpdate}
        />
      </div>
    );
  }
  
  return (
    <div className="animate-fadeIn">
      {isLoading ? (
        <div className="py-8 flex flex-col items-center justify-center">
          <div className="animate-pulse mb-4">
            <div className="w-12 h-12 rounded-full border-4 border-black border-t-transparent animate-spin"></div>
          </div>
          <p className="text-gray-600">Analyzing your project details...</p>
        </div>
      ) : error ? (
        <div className="bg-red-50 p-4 rounded-lg flex items-start mb-6">
          <AlertCircle className="text-red-500 mr-2 shrink-0 mt-0.5" size={20} />
          <div>
            <p className="text-red-700">{error}</p>
            <button 
              className="text-red-600 underline mt-2"
              onClick={onBack}
            >
              Go back and try again
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <h3 className="font-medium mb-2">Project Details</h3>
            <p className="text-gray-700 mb-2">
              <span className="font-medium">Description:</span> {state.workDescription?.description}
            </p>
            <p className="text-gray-700">
              <span className="font-medium">Timeframe:</span> {state.workDescription?.timeframe}
            </p>
          </div>
          
          {priceRange && (
            <PriceRangeWidget
              minPrice={priceRange.min}
              maxPrice={priceRange.max}
              confidence={priceRange.confidence}
            />
          )}
          
          <div className="bg-gray-50 rounded-lg p-4 my-6">
            <h3 className="font-medium flex items-center gap-2 mb-3">
              <Check size={18} /> What's Next
            </h3>
            <ul className="space-y-2 text-sm">
              <li className="flex items-start">
                <span className="text-green-500 mr-2">✓</span>
                <span>We'll gather competitive quotes from trusted professionals</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-500 mr-2">✓</span>
                <span>You'll receive detailed proposals within 24 hours</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-500 mr-2">✓</span>
                <span>No obligation to proceed with any quote</span>
              </li>
            </ul>
          </div>
          
          <div className="flex flex-col gap-3">
            <button
              onClick={onNext}
              className="w-full bg-black text-white px-4 py-3 rounded-lg font-medium transition-colors duration-300 hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isLoading || !!error}
            >
              Continue to Get Quotes
            </button>
            
            <button 
              onClick={onBack}
              className="w-full px-4 py-3 rounded-lg border border-gray-300 hover:border-gray-400 transition-colors flex items-center justify-center gap-2"
            >
              <ArrowLeft size={16} /> Back
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default PriceEstimate;